import { motion } from 'framer-motion';
import { sample } from 'lodash';
import { useEffect, useState, useCallback } from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { ArrowLeft, Bot, Swords } from 'lucide-react';
import styled, { keyframes } from 'styled-components';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';

import ChatLayout from './components/Game/ChatLayout';
import GameLayout from './components/Game/GameLayout';
import MyForm from './components/MyForm';
import CharacterListPage from './components/CharacterListPage';
import AdminPage from './pages/AdminPage';
import Leaderboard from './components/Leaderboard';
import HelpSystem from './components/Tutorial/HelpSystem';
import Tutorial from './components/Tutorial/TutorialOverlay';
import VisualBattleGuide from './components/Tutorial/VisualBattleGuide';

import TopBar from './components/Home/TopBar';
import CharacterLineup from './components/Home/CharacterLineup';
import Wordmark from './components/brand/Wordmark';

import {
    socket,
    API_URL,
    getStoredAuth,
    clearStoredAuth,
    refreshSocketAuth,
} from './socket';

/**
 * App.js - the route root + homepage shell.
 *
 * The homepage is a single "movie poster" viewport:
 *
 *   [ TopBar ..................................................... ]
 *   [                                                               ]
 *   [             ┌─────────────────────────────┐                   ]
 *   [             │  CharacterLineup (6 cutouts)│  <-- overlaps     ]
 *   [             │         MOVIE               │                   ]
 *   [             │        MAYHEM               │                   ]
 *   [             │  "FOUR FIGHTERS. TURN BY TURN. OUTPLAY."        ]
 *   [             └─────────────────────────────┘                   ]
 *   [                                                               ]
 *   [ DataStrip ................................................... ]
 *
 * Match-started state retains its current Row/Col layout since that's
 * Phase 3's work. Homepage → Match is a hard swap; we don't try to
 * transition the poster chrome into the arena chrome.
 */

// ---------------- global chrome --------------------------------------------

const shimmer = keyframes`
  0%, 100% { opacity: 0.35; }
  50% { opacity: 0.55; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.55; transform: scale(1.35); }
`;

const sweep = keyframes`
  0% { transform: translateX(-120%); }
  100% { transform: translateX(220%); }
`;

const Shell = styled.div`
  /* Locked to exactly one viewport. Previously used min-height: 100vh which
     let the shell grow whenever its flex children didn't shrink past their
     intrinsic content size, pushing the JOIN BATTLE CTA below the fold and
     making the body scroll. Hard-capping height + overflow: hidden + a
     min-height: 0 cascade on the flex children keeps the whole composition
     in-frame on every standard laptop height (720, 800, 900, 1080). */
  height: 100vh;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  color: var(--color-cream-100);
  /* Poster canvas: red-600 at top fading to red-800 at the bottom, with a
     subtle off-center amber spot to mimic a cinema key light. */
  background:
    radial-gradient(
      ellipse 80% 60% at 30% 40%,
      rgba(255, 177, 60, 0.12) 0%,
      transparent 60%
    ),
    linear-gradient(180deg,
      var(--color-red-600) 0%,
      var(--color-red-700) 45%,
      var(--color-red-800) 100%
    );
  overflow: hidden;

  /* homepage reveals the global grain at marketing opacity, and a hint of
     halftone behind the hero */
  --grain-opacity: 0.18;
  --halftone-opacity: 0.06;
`;

// vignette corners so the poster looks printed, not rendered
const Vignette = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
  background: radial-gradient(
    ellipse at center,
    transparent 55%,
    rgba(58, 10, 16, 0.6) 100%
  );
  mix-blend-mode: multiply;
  animation: ${shimmer} 6s ease-in-out infinite;
`;

const Main = styled.main`
  flex: 1 1 auto;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  padding: 0;
  /* CRITICAL: flex items default to min-height: auto which refuses to shrink
     below content size. Setting this to 0 lets Main and Hero shrink when the
     viewport is shorter than the sum of children, so the CTA stays onscreen. */
  min-height: 0;
`;

// the "stage" - grid stack so the wordmark cuts across the lineup
const Hero = styled.section`
  position: relative;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 var(--space-6);
  flex: 1 1 auto;
  display: grid;
  /* one column, one row - items stack in the same cell so the wordmark can
     cut across the character lineup. Default align/justify is 'stretch', so
     items fill the cell unless they opt out via justify-self/align-self. */
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  min-height: 0;
  /* Clip any accidental overflow (e.g. an extra-scaled cutout) so it can't
     push the page height and re-introduce the scroll. */
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 0 var(--space-3);
  }
`;

const LineupStage = styled.div`
  grid-column: 1;
  grid-row: 1;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: stretch;
  justify-content: center;
  z-index: 1;
  min-height: 0;
`;

/**
 * Title lockup. Anchored to the BOTTOM of the hero cell so the wordmark
 * cuts across the characters' torso/waist line the way a real mass poster
 * does, not across their faces. The tagline lives inside the same stage
 * (not floating free) so it can never get crushed between billing labels.
 */
const WordmarkStage = styled.div`
  grid-column: 1;
  grid-row: 1;
  z-index: 3;
  /* Size BOTH by viewport width (so it breathes on ultra-wide) AND viewport
     height (so it shrinks on short laptops where a fixed-px wordmark would
     eat the CTA zone). The 66vh cap keeps wordmark HEIGHT under ~25vh
     (aspect is 2.63:1, so width/2.63 = height) regardless of aspect ratio. */
  width: min(50vw, 66vh, 660px);
  justify-self: center;
  align-self: end;
  pointer-events: none;
  /* Clear zone above the billing row. Using clamp(vh) instead of fixed px
     so the wordmark lands at roughly the same spot on the character - lower
     chest / upper belly - whether the hero is 440px or 800px tall. */
  margin-bottom: clamp(90px, 14vh, 180px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);

  @media (max-width: 768px) {
    width: 84%;
  }
`;

const Tagline = styled(motion.p)`
  font-family: var(--font-heading);
  font-size: var(--size-sm);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-cream-200);
  margin: 0;
  white-space: nowrap;
  text-shadow: 0 2px 8px rgba(8, 7, 10, 0.6);
  /* amber underline decorator */
  &::after {
    content: '';
    display: block;
    width: 48px;
    height: 2px;
    background: var(--color-amber-500);
    margin: 8px auto 0;
  }

  @media (max-width: 768px) {
    font-size: var(--size-xs);
  }
`;

// ---------------- primary CTA band ----------------------------------------
//
// Sits between the poster hero and the data strip. This is THE action - the
// only amber, high-contrast element on the page after the wordmark. Flanked
// by a live queue indicator ("X PLAYERS READY") and quiet secondary links so
// the user knows this is a game they can join NOW.

const CtaBand = styled(motion.div)`
  position: relative;
  z-index: 4;
  flex-shrink: 0;
  width: 100%;
  padding: var(--space-3) var(--space-6) var(--space-3);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  /* subtle darkening wash so the amber button pops and the band reads as
     its own layer - not part of the poster's red canvas. */
  background:
    linear-gradient(180deg,
      rgba(8, 7, 10, 0) 0%,
      rgba(8, 7, 10, 0.32) 100%
    );

  @media (max-width: 768px) {
    padding: var(--space-3) var(--space-3) var(--space-3);
  }
`;

const CtaButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  width: 100%;
  max-width: 560px;
`;

const JoinBattleButton = styled(motion.button)`
  position: relative;
  overflow: hidden;
  background: var(--color-amber-500);
  color: var(--color-ink-950);
  border: none;
  padding: 14px 40px;
  font-family: var(--font-display);
  font-size: var(--size-lg);
  font-weight: var(--weight-regular);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  cursor: pointer;
  clip-path: var(--clip-button);
  display: inline-flex;
  align-items: center;
  gap: 12px;
  transition: transform var(--dur-base) var(--ease-out),
              background var(--dur-base) var(--ease-out),
              box-shadow var(--dur-base) var(--ease-out);
  box-shadow: var(--shadow-glow-amber), 0 10px 30px rgba(8, 7, 10, 0.45);

  svg { stroke-width: 2.5; }

  /* subtle diagonal shine so the button reads as "hot", like an on-air sign */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 40%;
    height: 100%;
    background: linear-gradient(
      100deg,
      transparent 20%,
      rgba(255, 255, 255, 0.35) 50%,
      transparent 80%
    );
    animation: ${sweep} 3.6s ease-in-out infinite;
    pointer-events: none;
  }

  &:hover,
  &:focus-visible {
    background: var(--color-amber-400);
    transform: translateY(-2px);
    outline: none;
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 12px 26px;
    font-size: var(--size-base);
    gap: 10px;
  }
`;

const PlaySoloButton = styled(motion.button)`
  position: relative;
  background: var(--color-ink-900);
  color: var(--color-cream-100);
  border: 1px solid rgba(103, 201, 122, 0.45);
  padding: 14px 28px;
  font-family: var(--font-display);
  font-size: var(--size-base);
  font-weight: var(--weight-regular);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  cursor: pointer;
  clip-path: var(--clip-button);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: transform var(--dur-base) var(--ease-out),
    background var(--dur-base) var(--ease-out),
    border-color var(--dur-base) var(--ease-out);
  box-shadow: 0 6px 20px rgba(8, 7, 10, 0.35);

  svg { stroke-width: 2.5; }

  &:hover,
  &:focus-visible {
    background: var(--color-ink-800);
    border-color: rgba(103, 201, 122, 0.75);
    transform: translateY(-2px);
    outline: none;
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: var(--size-sm);
  }
`;

const QueueStrip = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
  justify-content: center;
  font-family: var(--font-mono);
  font-size: var(--size-xs);
  font-weight: var(--weight-medium);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-cream-300);
`;

const QueueIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: #4ade80; /* lively green so "online" is obvious */
    box-shadow: 0 0 10px rgba(74, 222, 128, 0.6);
    animation: ${pulse} 1.6s var(--ease-in-out) infinite;
  }
`;

const QueueDivider = styled.span`
  width: 1px;
  height: 14px;
  background: rgba(246, 241, 228, 0.25);
`;

// ---------------- match layout (unchanged for Phase 2) ---------------------

/* Match state chrome — ink canvas so BattleLayout & ChatLayout read as the
   same stage. The pre-reskin purple/red/yellow gradient flashed through at
   the edges during match load; the ink base removes that. */
const MatchShell = styled(Container)`
  padding: 0;
  max-width: 100%;
  min-height: 100vh;
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  background: var(--color-ink-950);
`;

const GameContainer = styled.div`
  background: transparent;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
  height: 100vh;
  overflow: hidden;
`;

// legacy back-to-home chip, used only on /characters and /leaderboard
// (those pages get their own themed headers in Phase 4/5)
const LegacyBackChip = styled(Link)`
  position: fixed;
  top: 20px;
  left: 20px;
  background: var(--color-amber-500);
  color: var(--color-ink-950);
  padding: 10px 18px;
  text-decoration: none;
  box-shadow: var(--shadow-md);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-heading);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-wider);
  font-size: var(--size-xs);
  text-transform: uppercase;
  clip-path: var(--clip-button);
  z-index: 1000;
  transition: transform var(--dur-fast) var(--ease-out);

  &:hover {
    color: var(--color-ink-950);
    text-decoration: none;
    transform: translateY(-1px);
  }
`;

// ---------------- MainApp --------------------------------------------------

function MainApp() {
    const [username, setUsername] = useState(() => getStoredAuth().username || '');
    const [matchStarted, setIsMatchStarted] = useState(false);
    const [gameData, setGameData] = useState();
    const [battleLogs, setBattleLogs] = useState([]);
    const [battleAnimationData, setBattleAnimationData] = useState(null);
    const [devPreviewResult, setDevPreviewResult] = useState(null);

    // NEW: homepage-level UI state
    const [isLoginOpen, setIsLoginOpen] = useState(false);
    /** Drives which path is shown first in the match drawer: online PvP vs solo AI */
    const [lobbyMode, setLobbyMode] = useState('online');
    const [showTutorial, setShowTutorial] = useState(false);
    const [showBattleGuide, setShowBattleGuide] = useState(false);
    const [playersOnline, setPlayersOnline] = useState(0);
    const [isSignedIn, setIsSignedIn] = useState(() => !!getStoredAuth().token);
    const [signedInUsername, setSignedInUsername] = useState(() => getStoredAuth().username || '');

    // Expose match state globally so router-level components (e.g. the
    // HelpSystem FAB that survives on /characters and /leaderboard) can
    // hide themselves during a match without prop drilling.
    useEffect(() => {
        window.__inMatch = matchStarted;
        document.body.classList.toggle('in-match', !!matchStarted);
        return () => {
            window.__inMatch = false;
            document.body.classList.remove('in-match');
        };
    }, [matchStarted]);

    const isProduction = process.env.NODE_ENV === 'production';
    let bgMusics = [
        '/bgmusics/honor-and-sword-main-11222.mp3',
        '/bgmusics/powerful-energetic-sport-rock-trailer-122077.mp3',
        '/bgmusics/risk-136788.mp3'
    ];
    if (isProduction) {
        bgMusics = [
            'https://dvwhdo29g4dcg.cloudfront.net/honor.mp3',
            'https://dvwhdo29g4dcg.cloudfront.net/powerful.mp3',
            'https://dvwhdo29g4dcg.cloudfront.net/risk.mp3'
        ];
    }

    // socket wiring (connection, game start, player count)
    useEffect(() => {
        const onGameStart = (value) => {
            console.log(value);
            setIsMatchStarted(true);
            setGameData(value);
            setIsLoginOpen(false); // match found → dismiss the drawer
        };
        const onActivePlayersCount = (data) => {
            setPlayersOnline(data?.count ?? 0);
        };
        socket.on('game start', onGameStart);
        socket.on('active_players_count', onActivePlayersCount);
        return () => {
            socket.off('game start', onGameStart);
            socket.off('active_players_count', onActivePlayersCount);
        };
    }, []);

    // Keep TopBar's "signed in" hint in sync when MyForm mutates localStorage.
    // Simplest approach: listen to our storage events + a poll on drawer
    // open/close. Prevents a stale "Sign out <old name>" flashing on screen.
    useEffect(() => {
        const sync = () => {
            const { token, username: u } = getStoredAuth();
            setIsSignedIn(!!token);
            setSignedInUsername(u || '');
        };
        sync();
        window.addEventListener('storage', sync);
        return () => window.removeEventListener('storage', sync);
    }, [isLoginOpen]);

    /* First-time visitor sees the homepage Joyride once, ever. We set the
       flag the moment we schedule the open — so even if the user dismisses
       mid-tour (ESC, click-off, refresh) we never bother them with it
       again. Manual re-entry is still available via the Help FAB → "Tour
       the UI". Key is intentionally shared with `TutorialOverlay`'s
       finish handler and `MyForm`'s first-visit check so the three
       agree. */
    useEffect(() => {
        if (matchStarted) return;
        const seen = localStorage.getItem('movie-mayhem-tutorial-seen');
        if (!seen) {
            const t = setTimeout(() => {
                localStorage.setItem('movie-mayhem-tutorial-seen', 'true');
                setShowTutorial(true);
            }, 1200);
            return () => clearTimeout(t);
        }
    }, [matchStarted]);

    // DEV ONLY: keyboard shortcuts to preview game-end modal without playing
    // a match. Shift+V = Victory, Shift+D = Defeat, Shift+X = close.
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') return;

        const buildMockGameData = () => ({
            opponent: {
                name: 'TestOpponent',
                room: 'dev-room',
                team: [],
                selectedCharecter: {
                    name: 'DevChar', image: '', health: 100, modifiedHealth: 100,
                    attack: 50, defense: 50, speed: 50, joinedMoves: []
                }
            },
            userTeam: [{
                name: 'DevChar', image: '', health: 100, modifiedHealth: 100,
                attack: 50, defense: 50, speed: 50, joinedMoves: []
            }]
        });

        const handleDevShortcut = (e) => {
            if (!e.shiftKey) return;
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            const key = e.key.toLowerCase();
            if (!['v', 'd', 'x'].includes(key)) return;

            e.preventDefault();

            if (key === 'x') {
                console.log('[DEV] Closing preview');
                setDevPreviewResult(null);
                return;
            }

            const isVictory = key === 'v';
            const ratingChange = isVictory ? 24 : -18;
            const devName = username || 'DevPlayer';

            if (!matchStarted) {
                setUsername(devName);
                setGameData(buildMockGameData());
                setIsMatchStarted(true);
            }

            console.log(`[DEV] Previewing ${isVictory ? 'VICTORY' : 'DEFEAT'} modal`);
            setDevPreviewResult({
                result: isVictory ? 'victory' : 'defeat',
                message: isVictory
                    ? 'Victory! You have proven your mastery!'
                    : 'Defeat! Train harder!',
                winner: isVictory ? devName : 'TestOpponent',
                loser: isVictory ? 'TestOpponent' : devName,
                ratingChange,
                newRating: 1000 + ratingChange,
                show: true,
                _devPreview: true
            });
        };

        window.addEventListener('keydown', handleDevShortcut);
        return () => window.removeEventListener('keydown', handleDevShortcut);
    }, [matchStarted, username]);

    const onJoinBattle = useCallback(() => {
        setLobbyMode('online');
        setIsLoginOpen(true);
    }, []);
    const onPlaySolo = useCallback(() => {
        setLobbyMode('solo');
        setIsLoginOpen(true);
    }, []);
    /* "How to Play" and "How it works" both point at the mechanics guide —
       that's what players expect. The short UI Joyride (showTutorial) is
       reserved for first-visit auto-run and the Help FAB's "Tour the UI". */
    const onHowToPlay = useCallback(() => setShowBattleGuide(true), []);
    const onCloseLogin = useCallback(() => setIsLoginOpen(false), []);

    // Sign-out from top bar: wipe the local session, leave the drawer closed.
    // The actual server-side invalidation happens inside MyForm's own logout
    // when the user clicks sign-out there - this is just the quick-eject UX.
    const onTopBarSignOut = useCallback(() => {
        const { token } = getStoredAuth();
        clearStoredAuth();
        if (token) {
            fetch(`${API_URL}/api/auth/logout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token }),
            }).catch(() => { });
        }
        setIsSignedIn(false);
        setSignedInUsername('');
        setUsername('');
        refreshSocketAuth();
    }, []);

    // ----------------- render: match state ---------------------------------

    if (matchStarted) {
        return (
            <MatchShell fluid>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4 }}
                    style={{ height: '100vh', overflow: 'hidden' }}
                >
                    <GameContainer>
                        <Row className="g-0 h-100">
                            <Col md={8} style={{ height: '100vh' }}>
                                <GameLayout
                                    username={username}
                                    gameData={gameData}
                                    battleLogs={battleLogs}
                                    setBattleLogs={setBattleLogs}
                                    setBattleAnimationData={setBattleAnimationData}
                                    battleAnimationData={battleAnimationData}
                                    devPreviewResult={devPreviewResult}
                                />
                            </Col>
                            <Col md={4} style={{ height: '100vh', padding: '10px' }}>
                                <ChatLayout
                                    username={username}
                                    gameData={gameData}
                                    battleLogs={battleLogs}
                                />
                            </Col>
                        </Row>
                    </GameContainer>

                    <audio autoPlay loop>
                        <source src={sample(bgMusics)} type="audio/mpeg" />
                    </audio>
                </motion.div>
            </MatchShell>
        );
    }

    // ----------------- render: homepage ------------------------------------

    return (
        <Shell>
            <Vignette aria-hidden="true" />

            <TopBar
                onHowToPlay={onHowToPlay}
                isSignedIn={isSignedIn}
                signedInUsername={signedInUsername}
                onSignOut={onTopBarSignOut}
            />

            <Main>
                <Hero>
                    <LineupStage>
                        <CharacterLineup />
                    </LineupStage>
                    <WordmarkStage>
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                            style={{ width: '100%' }}
                        >
                            <Wordmark />
                        </motion.div>
                        <Tagline
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.9, duration: 0.5 }}
                        >
                            Four fighters. Turn by turn. Outplay.
                        </Tagline>
                    </WordmarkStage>
                </Hero>

                <CtaBand
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.05, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                    <CtaButtonRow>
                        <JoinBattleButton
                            type="button"
                            onClick={onJoinBattle}
                            className="find-match-button"
                            whileTap={{ scale: 0.97 }}
                            aria-label={isSignedIn ? `Queue for online match as ${signedInUsername}` : 'Queue for online match'}
                        >
                            <Swords size={22} />
                            {isSignedIn ? `Find match (${signedInUsername})` : 'Find online match'}
                        </JoinBattleButton>
                        <PlaySoloButton
                            type="button"
                            onClick={onPlaySolo}
                            whileTap={{ scale: 0.97 }}
                            aria-label="Play solo versus AI, no queue"
                        >
                            <Bot size={22} />
                            Play vs AI
                        </PlaySoloButton>
                    </CtaButtonRow>

                    <QueueStrip>
                        <QueueIndicator className="players-online">
                            {playersOnline === 1
                                ? '1 player online'
                                : `${playersOnline} players online`}
                        </QueueIndicator>
                        <QueueDivider />
                        <span>Free • Cross-platform • Rated 4v4</span>
                    </QueueStrip>

                </CtaBand>
            </Main>

            <MyForm
                isOpen={isLoginOpen}
                onClose={onCloseLogin}
                username={username}
                setUsername={setUsername}
                preferredMode={lobbyMode}
                openTutorialExternally={() => setShowTutorial(true)}
            />

            <Tutorial
                isOpen={showTutorial}
                onClose={() => setShowTutorial(false)}
                tutorialType="main"
            />

            <VisualBattleGuide
                isOpen={showBattleGuide}
                onClose={() => setShowBattleGuide(false)}
            />
        </Shell>
    );
}

// ---------------- Router + legacy routes -----------------------------------

export default function App() {
    return (
        <Router>
            <Routes>
                <Route path="/" element={<MainApp />} />
                <Route
                    path="/characters"
                    element={
                        <div>
                            <LegacyBackChip to="/">
                                <ArrowLeft size={14} />
                                Back
                            </LegacyBackChip>
                            <CharacterListPage />
                            <HelpSystem />
                        </div>
                    }
                />
                <Route
                    path="/leaderboard"
                    element={
                        <div>
                            <LegacyBackChip to="/">
                                <ArrowLeft size={14} />
                                Back
                            </LegacyBackChip>
                            <Leaderboard />
                            <HelpSystem />
                        </div>
                    }
                />
                <Route path="/admin" element={<AdminPage />} />
            </Routes>
        </Router>
    );
}
